import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, Collapse, Switch, Chip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Menu as MenuIcon, Inventory2, People, SwapHoriz, Dashboard, ExpandLess, ExpandMore, Event, Groups, Visibility, VisibilityOff, Settings as SettingsIcon, PersonOutline, History as HistoryIcon, AccountBalance as BankIcon, Description, RateReview, CheckCircle, Assessment, Assignment, Category, CalendarMonth, MoveToInbox, AdminPanelSettings, VpnKey } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import UserMenu from './UserMenu';
import axios from 'axios';
import API_BASE_URL from '../config/api';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [collapseOpen, setCollapseOpen] = React.useState(false);
  const [programCollapseOpen, setProgramCollapseOpen] = React.useState(false);
  const [historyCollapseOpen, setHistoryCollapseOpen] = React.useState(false);
  const [settingsCollapseOpen, setSettingsCollapseOpen] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState<number | null>(null);
  const [allUsers, setAllUsers] = React.useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user role from localStorage
  const user = React.useMemo(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  }, []);

  // Check if user has SUPER_ADMIN role (support both role and roles array)
  const isAdmin = user?.roles?.includes('SUPER_ADMIN') || user?.role === 'SUPER_ADMIN';
  
  // Check if viewing as another user
  const viewAsUserData = localStorage.getItem('viewAsUser');
  const currentViewUser = viewAsUserData ? JSON.parse(viewAsUserData) : user;
  
  // Use the current view user's role for menu permissions (use first role from roles array)
  const userRole = currentViewUser?.roles?.[0] || currentViewUser?.role || 'KOMISI';

  // Fetch all users for the selector
  React.useEffect(() => {
    const fetchUsers = async () => {
      if (isAdmin) {
        try {
          // Request with large limit to get all users
          const response = await axios.get(`${API_BASE_URL}/api/users?limit=1000`);
          
          // Handle both paginated and non-paginated responses
          let usersArray = [];
          if (Array.isArray(response.data)) {
            usersArray = response.data;
          } else if (response.data.users && Array.isArray(response.data.users)) {
            usersArray = response.data.users;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            usersArray = response.data.data;
          }
          
          console.log('Loaded users for selector:', usersArray.length);
          setAllUsers(usersArray);
          
          // Set initial selected user
          const viewAsUserData = localStorage.getItem('viewAsUser');
          if (viewAsUserData) {
            const viewUser = JSON.parse(viewAsUserData);
            setSelectedUserId(viewUser.id);
          } else {
            setSelectedUserId(user?.id || null);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      }
    };
    fetchUsers();
  }, [isAdmin, user?.id]);

  // Handle user selection change
  const handleUserChange = async (userId: number) => {
    const selectedUser = allUsers.find(u => u.id === userId);
    if (selectedUser) {
      try {
        // Save the original admin user if not already saved
        if (!localStorage.getItem('originalAdminUser')) {
          localStorage.setItem('originalAdminUser', localStorage.getItem('user') || '{}');
          localStorage.setItem('originalAdminToken', localStorage.getItem('token') || '');
        }
        
        // Get current admin token
        const adminToken = localStorage.getItem('token');
        
        // Use admin impersonate endpoint (no password needed)
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/impersonate`,
          { userId: selectedUser.id },
          {
            headers: {
              'Authorization': `Bearer ${adminToken}`
            }
          }
        );
        
        // Store the new token and user
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.removeItem('viewAsUser'); // Clear any old viewAsUser
        
        // Reload to apply changes
        window.location.href = '/dashboard';
      } catch (error: any) {
        console.error('Failed to switch user:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        alert(`Failed to switch user: ${errorMessage}\n\nMake sure you are logged in as SUPER_ADMIN.`);
      }
    }
  };

  // Reset to original admin user
  const resetToOriginalUser = () => {
    // Restore original admin credentials
    const originalUser = localStorage.getItem('originalAdminUser');
    const originalToken = localStorage.getItem('originalAdminToken');
    
    if (originalUser && originalToken) {
      localStorage.setItem('user', originalUser);
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('originalAdminUser');
      localStorage.removeItem('originalAdminToken');
    }
    
    // Redirect to login page
    localStorage.removeItem('viewAsUser');
    window.location.href = '/dashboard';
  };

  // Get menu permissions from localStorage
  const menuPermissions = React.useMemo(() => {
    const savedPermissions = localStorage.getItem('menuPermissions');
    if (savedPermissions) {
      try {
        const permissions = JSON.parse(savedPermissions);
        
        // Auto-migrate: If any permission doesn't have KOMISI property, add it based on defaults
        let needsMigration = false;
        const migratedPermissions = permissions.map((perm: any) => {
          if (typeof perm.roles.KOMISI === 'undefined') {
            needsMigration = true;
            // Set KOMISI to true for these menus (from Settings defaults)
            const komisiAccessMenus = [
              'dashboard', 'personalia-komisi', 'equipment-history', 'equipment-movement',
              'programs', 'activities', 'proposal-history'
            ];
            return {
              ...perm,
              roles: {
                ...perm.roles,
                KOMISI: komisiAccessMenus.includes(perm.id)
              }
            };
          }
          return perm;
        });
        
        // Save migrated permissions back to localStorage
        if (needsMigration) {
          console.log('Auto-migrating menu permissions to include KOMISI role');
          localStorage.setItem('menuPermissions', JSON.stringify(migratedPermissions));
          return migratedPermissions;
        }
        
        return permissions;
      } catch (error) {
        console.error('Error loading permissions:', error);
        return null;
      }
    }
    return null;
  }, []); // Keep empty dependency since localStorage doesn't change reactively

  // Check if user has permission to see a menu item
  const hasPermission = React.useCallback((menuId: string): boolean => {
    // Admin (actual logged-in user) always has full access to all menus
    if (user?.roles?.includes('SUPER_ADMIN') || user?.role === 'SUPER_ADMIN') return true;
    
    // Get all user roles (support both roles array and single role)
    const allUserRoles = user?.roles || (user?.role ? [user.role] : []);
    
    if (!menuPermissions) {
      // Default permissions when no settings are configured
      const defaultAdminOnly = ['user-management', 'manajemen-personalia-komisi', 'inventory-management', 'categories', 'program-categories'];
      const defaultAll = ['dashboard', 'personalia-komisi', 'equipment-history', 'equipment-movement', 'programs', 'activities'];
      
      // History menu defaults (specific per role) - check if user has ANY of these roles
      if (menuId === 'proposal-history') return allUserRoles.includes('KOMISI');
      if (menuId === 'review-history') return allUserRoles.includes('REVIEWER');
      if (menuId === 'approval-history') return allUserRoles.includes('APPROVAL');
      if (menuId === 'transfer-history') return allUserRoles.includes('KASIR');
      
      if (defaultAll.includes(menuId)) return true;
      if (defaultAdminOnly.includes(menuId)) return false;
      return true; // Show other items by default
    }
    const permission = menuPermissions.find((p: any) => p.id === menuId);
    if (!permission) {
      // If menu not in settings, use default logic
      const defaultAdminOnly = ['user-management', 'manajemen-personalia-komisi', 'inventory-management', 'categories', 'program-categories'];
      const defaultAll = ['dashboard', 'personalia-komisi', 'equipment-history', 'equipment-movement', 'programs', 'activities'];
      
      // History menu defaults (specific per role) - check if user has ANY of these roles
      if (menuId === 'proposal-history') return allUserRoles.includes('KOMISI');
      if (menuId === 'review-history') return allUserRoles.includes('REVIEWER');
      if (menuId === 'approval-history') return allUserRoles.includes('APPROVAL');
      if (menuId === 'transfer-history') return allUserRoles.includes('KASIR');
      
      if (defaultAll.includes(menuId)) return true;
      if (defaultAdminOnly.includes(menuId)) return false;
      return true;
    }
    
    // Check if ANY of the user's roles has permission for this menu
    const hasAccess = allUserRoles.some((role: string) => permission.roles[role] === true);
    return hasAccess;
  }, [menuPermissions, user?.roles, user?.role]);

  const subMenuItems = [
    { id: 'inventory-management', text: 'Manajemen Inventaris', icon: <Inventory2 sx={{ color: '#667eea' }} />, path: '/inventory-management' },
    { id: 'equipment-movement', text: 'Peminjaman/Pengembalian', icon: <MoveToInbox sx={{ color: '#10b981' }} />, path: '/equipment/movement' },
    { id: 'categories', text: 'Kategori', icon: <Category sx={{ color: '#f59e0b' }} />, path: '/categories' },
  ];

  // Check if user has at least one inventory menu permission
  const hasInventoryMenuAccess = React.useMemo(() => {
    return subMenuItems.some(item => hasPermission(item.id));
  }, [subMenuItems, hasPermission]);

  const programMenuItems = [
    { id: 'programs', text: 'Daftar Program', icon: <Assessment sx={{ color: '#667eea' }} />, path: '/programs' },
    { id: 'activities', text: 'Daftar Kegiatan', icon: <CalendarMonth sx={{ color: '#10b981' }} />, path: '/activities' },
    { id: 'program-categories', text: 'Kategori', icon: <Category sx={{ color: '#f59e0b' }} />, path: '/program-categories' },
  ];

  // Check if user has at least one program menu permission
  const hasProgramMenuAccess = React.useMemo(() => {
    return programMenuItems.some(item => hasPermission(item.id));
  }, [programMenuItems, hasPermission]);

  const historyMenuItems = [
    { id: 'equipment-history', text: 'Riwayat Peralatan', icon: <SwapHoriz sx={{ color: '#00d4ff' }} />, path: '/equipment/history' },
    { id: 'proposal-history', text: 'Riwayat Proposal', icon: <Description sx={{ color: '#667eea' }} />, path: '/history/proposals' },
    { id: 'review-history', text: 'Riwayat Review', icon: <RateReview sx={{ color: '#f59e0b' }} />, path: '/history/reviews' },
    { id: 'approval-history', text: 'Riwayat Approval Anggaran', icon: <CheckCircle sx={{ color: '#10b981' }} />, path: '/history/approvals' },
    { id: 'transfer-history', text: 'Riwayat Transfer', icon: <BankIcon sx={{ color: '#3b82f6' }} />, path: '/history/transfer' },
  ];

  // Check if user has at least one history menu permission
  const hasHistoryMenuAccess = React.useMemo(() => {
    return historyMenuItems.some(item => hasPermission(item.id));
  }, [historyMenuItems, hasPermission]);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(true)}
            sx={{ 
              mr: 2,
              '&:hover': {
                background: 'rgba(102, 126, 234, 0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo */}
          <Box
            component="img"
            src="/logo.svg"
            alt="Logo"
            sx={{
              height: 40,
              width: 40,
              marginRight: 2,
              cursor: 'pointer',
              display: { xs: 'none', sm: 'flex' },
              objectFit: 'contain',
              padding: '4px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.6)',
                background: 'rgba(255, 255, 255, 0.15)',
              }
            }}
            onClick={() => navigate('/')}
          />
          
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              display: { xs: 'none', md: 'block' }
            }}
          >
            Pusat Pastoral Management System
          </Typography>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 700,
              display: { xs: 'block', md: 'none' },
              color: 'white',
            }}
          >
            PP Management
          </Typography>
          
          {/* User Selector - Admin Only */}
          {isAdmin && localStorage.getItem('token') && (
            <FormControl sx={{ mr: 2, minWidth: 200 }} size="small">
              <Select
                value={selectedUserId || user?.id || ''}
                onChange={(e) => handleUserChange(Number(e.target.value))}
                displayEmpty
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    py: 1
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                  }
                }}
              >
                <MenuItem value={user?.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonOutline fontSize="small" />
                    <Typography>Saya ({user?.name})</Typography>
                  </Box>
                </MenuItem>
                {allUsers.length > 0 ? (
                  allUsers.filter(u => u.id !== user?.id).map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <PersonOutline fontSize="small" />
                        <Typography>{u.name}</Typography>
                        {/* Display all roles for the user */}
                        {(u.roles && u.roles.length > 0 ? u.roles : [u.role]).map((role: string, idx: number) => (
                          <Chip 
                            key={idx}
                            label={role} 
                            size="small" 
                            sx={{ ml: 0.5 }}
                          />
                        ))}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">Loading users...</Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
          
          {/* Back to Admin Button - Shows when viewing as another user */}
          {!isAdmin && localStorage.getItem('originalAdminUser') && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<PersonOutline />}
              onClick={resetToOriginalUser}
              sx={{ 
                mr: 2,
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                }
              }}
            >
              Back to Admin
            </Button>
          )}
          
          {/* Show current view indicator */}
          {isAdmin && viewAsUserData && (
            <Chip
              icon={<Visibility />}
              label={`Viewing as: ${currentViewUser?.name}`}
              color="warning"
              onDelete={resetToOriginalUser}
              sx={{ mr: 2, fontWeight: 'bold' }}
            />
          )}
          
          {localStorage.getItem('token') ? (
            <UserMenu />
          ) : (
            <Button color="inherit" onClick={() => navigate('/login')}>
              Masuk
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            color: 'white',
            boxShadow: '4px 0 20px rgba(0,0,0,0.3)',
          }
        }}
      >
        {/* Header Section with Gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '24px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="Logo"
              sx={{
                width: 48,
                height: 48,
                objectFit: 'contain',
                padding: '8px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              }}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.5px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                PUSAT PASTORAL
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '11px' }}>
                Management System
              </Typography>
            </Box>
          </Box>
        </Box>

        <List sx={{ px: 2, py: 2 }}>
          {/* Dashboard Menu Item */}
          {hasPermission('dashboard') && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <Button
                onClick={() => {
                  navigate('/');
                  setDrawerOpen(false);
                }}
                sx={{ 
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '12px 16px',
                  color: 'white',
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                  textTransform: 'none',
                  background: location.pathname === '/' 
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: location.pathname === '/' ? '1px solid rgba(102, 126, 234, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: location.pathname === '/' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: location.pathname === '/' 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.5) 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(4px) scale(1.02)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  },
                  '&::before': location.pathname === '/' ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '4px',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '0 4px 4px 0',
                  } : {}
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  '& svg': { 
                    fontSize: 24,
                    color: '#2196f3',
                    filter: location.pathname === '/' ? 'drop-shadow(0 0 8px rgba(102, 126, 234, 0.6))' : 'none'
                  }
                }}>
                  <Dashboard />
                </ListItemIcon>
                <ListItemText 
                  primary="Dashboard" 
                  primaryTypographyProps={{
                    fontSize: '15px',
                    fontWeight: location.pathname === '/' ? 600 : 500,
                  }}
                />
              </Button>
            </ListItem>
          )}

          {/* Manajemen Pengguna Menu Item - Admin Only */}
          {hasPermission('user-management') && (user?.roles?.includes('SUPER_ADMIN') || user?.role === 'SUPER_ADMIN') && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <Button
                onClick={() => {
                  navigate('/users');
                  setDrawerOpen(false);
                }}
                sx={{ 
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '12px 16px',
                  color: 'white',
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                  textTransform: 'none',
                  background: location.pathname === '/users' 
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: location.pathname === '/users' ? '1px solid rgba(102, 126, 234, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: location.pathname === '/users' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: location.pathname === '/users' 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.5) 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(4px) scale(1.02)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  },
                  '&::before': location.pathname === '/users' ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '4px',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '0 4px 4px 0',
                  } : {}
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  '& svg': { 
                    fontSize: 24,
                    color: '#667eea',
                    filter: location.pathname === '/users' ? 'drop-shadow(0 0 8px rgba(102, 126, 234, 0.6))' : 'none'
                  }
                }}>
                  <People />
                </ListItemIcon>
                <ListItemText 
                  primary="Manajemen Pengguna" 
                  primaryTypographyProps={{
                    fontSize: '15px',
                    fontWeight: location.pathname === '/users' ? 600 : 500,
                  }}
                />
              </Button>
            </ListItem>
          )}

          {/* Personalia Komisi Menu Item */}
          {hasPermission('personalia-komisi') && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <Button
                onClick={() => {
                  navigate('/personalia-komisi');
                  setDrawerOpen(false);
                }}
                sx={{ 
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '12px 16px',
                  color: 'white',
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                  textTransform: 'none',
                  background: location.pathname === '/personalia-komisi' 
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: location.pathname === '/personalia-komisi' ? '1px solid rgba(102, 126, 234, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: location.pathname === '/personalia-komisi' ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: location.pathname === '/personalia-komisi' 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.5) 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(4px) scale(1.02)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  },
                  '&::before': location.pathname === '/personalia-komisi' ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '4px',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '0 4px 4px 0',
                  } : {}
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  '& svg': { 
                    fontSize: 24,
                    color: '#764ba2',
                    filter: location.pathname === '/personalia-komisi' ? 'drop-shadow(0 0 8px rgba(102, 126, 234, 0.6))' : 'none'
                  }
                }}>
                  <Groups />
                </ListItemIcon>
                <ListItemText 
                  primary="Personalia Komisi" 
                  primaryTypographyProps={{
                    fontSize: '15px',
                    fontWeight: location.pathname === '/personalia-komisi' ? 600 : 500,
                  }}
                />
              </Button>
            </ListItem>
          )}

          {/* Collapsible Manajemen Program Menu */}
          {hasProgramMenuAccess && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <Button
                  onClick={() => setProgramCollapseOpen(!programCollapseOpen)}
                  sx={{ 
                    width: '100%',
                    justifyContent: 'flex-start',
                    padding: '12px 16px',
                    color: 'white',
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 15px rgba(0,212,255,0.2)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Event sx={{ 
                      fontSize: 24,
                      color: '#9c27b0'
                    }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Manajemen Program" 
                    primaryTypographyProps={{ fontSize: '15px', fontWeight: 500 }}
                  />
                  {programCollapseOpen ? <ExpandLess sx={{ color: '#00d4ff' }} /> : <ExpandMore />}
                </Button>
              </ListItem>

              <Collapse in={programCollapseOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2, position: 'relative' }}>
              {/* Connector Line */}
              <Box sx={{
                position: 'absolute',
                left: '7px',
                top: '20px',
                bottom: '8px',
                width: '2px',
                background: 'linear-gradient(180deg, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 100%)',
              }} />
              
              {programMenuItems.filter(item => hasPermission(item.id)).map((item, index) => (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <Button
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    sx={{ 
                      width: '100%',
                      justifyContent: 'flex-start',
                      padding: '10px 16px',
                      paddingLeft: '16px',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      position: 'relative',
                      background: location.pathname === item.path 
                        ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: location.pathname === item.path ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid transparent',
                      boxShadow: location.pathname === item.path ? '0 2px 10px rgba(0, 212, 255, 0.2)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(0, 212, 255, 0.15)',
                        transform: 'translateX(4px)',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                      },
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: location.pathname === item.path ? '#00d4ff' : 'rgba(255,255,255,0.3)',
                        fontSize: '20px',
                        lineHeight: '1',
                        transition: 'all 0.3s ease',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      '& svg': { 
                        fontSize: 22,
                        opacity: location.pathname === item.path ? 1 : 0.85,
                        filter: location.pathname === item.path ? 'drop-shadow(0 0 8px currentColor)' : 'none',
                        transition: 'all 0.3s ease'
                      }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: location.pathname === item.path ? 600 : 450,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </Button>
                </ListItem>
              ))}
            </List>
          </Collapse>
            </>
          )}

          {/* Collapsible Peminjaman Barang Menu */}
          {hasInventoryMenuAccess && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
            <Button
              onClick={() => setCollapseOpen(!collapseOpen)}
              sx={{ 
                width: '100%',
                justifyContent: 'flex-start',
                padding: '12px 16px',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 4px 15px rgba(0,212,255,0.2)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Inventory2 sx={{ 
                  fontSize: 24,
                  color: '#4caf50'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="Peminjaman Barang" 
                primaryTypographyProps={{ fontSize: '15px', fontWeight: 500 }}
              />
              {collapseOpen ? <ExpandLess sx={{ color: '#00d4ff' }} /> : <ExpandMore />}
            </Button>
          </ListItem>

          <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2, position: 'relative' }}>
              {/* Connector Line */}
              <Box sx={{
                position: 'absolute',
                left: '7px',
                top: '20px',
                bottom: '8px',
                width: '2px',
                background: 'linear-gradient(180deg, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 100%)',
              }} />
              
              {subMenuItems.filter(item => hasPermission(item.id)).map((item, index) => (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <Button
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    sx={{ 
                      width: '100%',
                      justifyContent: 'flex-start',
                      padding: '10px 16px',
                      paddingLeft: '16px',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      position: 'relative',
                      background: location.pathname === item.path 
                        ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: location.pathname === item.path ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid transparent',
                      boxShadow: location.pathname === item.path ? '0 2px 10px rgba(0, 212, 255, 0.2)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(0, 212, 255, 0.15)',
                        transform: 'translateX(4px)',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                      },
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: location.pathname === item.path ? '#00d4ff' : 'rgba(255,255,255,0.3)',
                        fontSize: '20px',
                        lineHeight: '1',
                        transition: 'all 0.3s ease',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      '& svg': { 
                        fontSize: 22,
                        opacity: location.pathname === item.path ? 1 : 0.85,
                        filter: location.pathname === item.path ? 'drop-shadow(0 0 8px currentColor)' : 'none',
                        transition: 'all 0.3s ease'
                      }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: location.pathname === item.path ? 600 : 450,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </Button>
                </ListItem>
              ))}
            </List>
          </Collapse>
            </>
          )}

          {/* Collapsible History Menu - KOMISI, KASIR and ADMIN */}
          {hasHistoryMenuAccess && (
            <>
              <ListItem disablePadding sx={{ mb: 1 }}>
            <Button
              onClick={() => setHistoryCollapseOpen(!historyCollapseOpen)}
              sx={{ 
                width: '100%',
                justifyContent: 'flex-start',
                padding: '12px 16px',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 4px 15px rgba(0,212,255,0.2)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HistoryIcon sx={{ 
                  fontSize: 24,
                  color: '#00bcd4'
                }} />
              </ListItemIcon>
              <ListItemText 
                primary="History" 
                primaryTypographyProps={{ fontSize: '15px', fontWeight: 500 }}
              />
              {historyCollapseOpen ? <ExpandLess sx={{ color: '#00d4ff' }} /> : <ExpandMore />}
            </Button>
          </ListItem>

          <Collapse in={historyCollapseOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 2, position: 'relative' }}>
              {/* Connector Line */}
              <Box sx={{
                position: 'absolute',
                left: '7px',
                top: '20px',
                bottom: '8px',
                width: '2px',
                background: 'linear-gradient(180deg, rgba(0,212,255,0.3) 0%, rgba(0,212,255,0) 100%)',
              }} />
              
              {historyMenuItems.filter(item => hasPermission(item.id)).map((item, index) => (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <Button
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    sx={{ 
                      width: '100%',
                      justifyContent: 'flex-start',
                      padding: '10px 16px',
                      paddingLeft: '16px',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      position: 'relative',
                      background: location.pathname === item.path 
                        ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: location.pathname === item.path ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid transparent',
                      boxShadow: location.pathname === item.path ? '0 2px 10px rgba(0, 212, 255, 0.2)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(0, 212, 255, 0.15)',
                        transform: 'translateX(4px)',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                      },
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: location.pathname === item.path ? '#00d4ff' : 'rgba(255,255,255,0.3)',
                        fontSize: '20px',
                        lineHeight: '1',
                        transition: 'all 0.3s ease',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      '& svg': { 
                        fontSize: 22,
                        opacity: location.pathname === item.path ? 1 : 0.85,
                        filter: location.pathname === item.path ? 'drop-shadow(0 0 8px currentColor)' : 'none',
                        transition: 'all 0.3s ease'
                      }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: location.pathname === item.path ? 600 : 450,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </Button>
                </ListItem>
              ))}
            </List>
          </Collapse>
            </>
          )}

          {/* Settings Menu Item - Admin Only */}
          {(user?.roles?.includes('SUPER_ADMIN') || user?.role === 'SUPER_ADMIN') && (
            <>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <Button
                onClick={() => setSettingsCollapseOpen(!settingsCollapseOpen)}
                sx={{ 
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '12px 16px',
                  color: 'white',
                  borderRadius: '12px',
                  textTransform: 'none',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 4px 15px rgba(102,126,234,0.2)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsIcon sx={{ 
                    fontSize: 24,
                    color: '#667eea'
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Pengaturan" 
                  primaryTypographyProps={{ fontSize: '15px', fontWeight: 500 }}
                />
                {settingsCollapseOpen ? <ExpandLess sx={{ color: '#667eea' }} /> : <ExpandMore />}
              </Button>
            </ListItem>
            
            <Collapse in={settingsCollapseOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2, position: 'relative' }}>
                {/* Connector Line */}
                <Box sx={{
                  position: 'absolute',
                  left: '7px',
                  top: '20px',
                  bottom: '8px',
                  width: '2px',
                  background: 'linear-gradient(180deg, rgba(102,126,234,0.3) 0%, rgba(102,126,234,0) 100%)',
                }} />
                
                {/* General Settings Sub-menu */}
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Button
                    onClick={() => {
                      navigate('/settings/general');
                      setDrawerOpen(false);
                    }}
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      padding: '10px 16px',
                      paddingLeft: '16px',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      position: 'relative',
                      background: location.pathname === '/settings/general'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: location.pathname === '/settings/general' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid transparent',
                      boxShadow: location.pathname === '/settings/general' ? '0 2px 10px rgba(102, 126, 234, 0.2)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.15)',
                        transform: 'translateX(4px)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                      },
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: location.pathname === '/settings/general' ? '#667eea' : 'rgba(255,255,255,0.3)',
                        fontSize: '20px',
                        lineHeight: '1',
                        transition: 'all 0.3s ease',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      '& svg': { 
                        fontSize: 22,
                        opacity: location.pathname === '/settings/general' ? 1 : 0.85,
                        filter: location.pathname === '/settings/general' ? 'drop-shadow(0 0 8px currentColor)' : 'none',
                        transition: 'all 0.3s ease'
                      }
                    }}>
                      <AdminPanelSettings sx={{ color: '#9c27b0' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pengaturan Utama"
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: location.pathname === '/settings/general' ? 600 : 450,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </Button>
                </ListItem>

                {/* Access Rights Settings Sub-menu */}
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Button
                    onClick={() => {
                      navigate('/settings');
                      setDrawerOpen(false);
                    }}
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      padding: '10px 16px',
                      paddingLeft: '16px',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      position: 'relative',
                      background: location.pathname === '/settings'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: location.pathname === '/settings' ? '1px solid rgba(102, 126, 234, 0.4)' : '1px solid transparent',
                      boxShadow: location.pathname === '/settings' ? '0 2px 10px rgba(102, 126, 234, 0.2)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.15)',
                        transform: 'translateX(4px)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                      },
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: location.pathname === '/settings' ? '#667eea' : 'rgba(255,255,255,0.3)',
                        fontSize: '20px',
                        lineHeight: '1',
                        transition: 'all 0.3s ease',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 36,
                      '& svg': { 
                        fontSize: 22,
                        opacity: location.pathname === '/settings' ? 1 : 0.85,
                        filter: location.pathname === '/settings' ? 'drop-shadow(0 0 8px currentColor)' : 'none',
                        transition: 'all 0.3s ease'
                      }
                    }}>
                      <VpnKey sx={{ color: '#3b82f6' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pengaturan Hak Akses"
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: location.pathname === '/settings' ? 600 : 450,
                        letterSpacing: '0.2px'
                      }}
                    />
                  </Button>
                </ListItem>
              </List>
            </Collapse>
            </>
          )}
        </List>

        {/* Footer Section */}
        <Box sx={{
          mt: 'auto',
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.2)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}>
              {currentViewUser?.name?.charAt(0).toUpperCase() || '👤'}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5 }}>
                {currentViewUser?.name || 'User'}
              </Typography>
              {/* Display all roles */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(currentViewUser?.roles && currentViewUser.roles.length > 0 
                  ? currentViewUser.roles 
                  : [currentViewUser?.role || 'KOMISI']
                ).map((role: string, index: number) => (
                  <Chip 
                    key={index}
                    label={role} 
                    size="small" 
                    sx={{ 
                      height: '20px',
                      fontSize: '10px',
                      background: role === 'SUPER_ADMIN' 
                        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.3) 100%)'
                        : role === 'REVIEWER'
                        ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(217, 119, 6, 0.3) 100%)'
                        : role === 'APPROVAL'
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%)'
                        : role === 'KASIR'
                        ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(124, 58, 237, 0.3) 100%)'
                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(37, 99, 235, 0.3) 100%)',
                      color: 'white',
                      border: role === 'SUPER_ADMIN' 
                        ? '1px solid rgba(239, 68, 68, 0.5)'
                        : role === 'REVIEWER'
                        ? '1px solid rgba(245, 158, 11, 0.5)'
                        : role === 'APPROVAL'
                        ? '1px solid rgba(16, 185, 129, 0.5)'
                        : role === 'KASIR'
                        ? '1px solid rgba(139, 92, 246, 0.5)'
                        : '1px solid rgba(59, 130, 246, 0.5)',
                      fontWeight: 600,
                    }} 
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #16213e 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container maxWidth={false} sx={{ position: 'relative', zIndex: 1, px: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
